'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { applyHeroImageStyle } from '@/lib/club-hero'

/**
 * Regolazione dello sfondo dell'header della pagina club.
 *
 * Pulsante discreto in **basso a destra** dell'header (visibile a chi gestisce il club):
 * apre un pannello con la scelta dell'immagine, il **joystick 2D per il focus** e lo
 * zoom. L'anteprima è **live sull'header vero**: si aggiornano gli stili
 * dell'elemento `[data-hero-image]`, quindi si vede subito il risultato.
 *
 * Il pannello è reso in un **portal su `document.body`**: dentro l'header verrebbe
 * tagliato dal suo `overflow-hidden`.
 */

interface HeroSettings {
  imageUrl: string
  positionX: number
  positionY: number
  zoom: number
}

interface MediaAsset {
  id: number
  url: string
  originalName: string
  width: number | null
  height: number | null
}

export default function HeroEditor({ clubId, initial }: { clubId: number; initial: HeroSettings | null }) {
  const router = useRouter()
  const [mounted, setMounted] = useState(false)
  const [open, setOpen] = useState(false)
  const [media, setMedia] = useState<MediaAsset[]>([])
  const [loadingList, setLoadingList] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [hero, setHero] = useState<HeroSettings | null>(initial)
  const padRef = useRef<HTMLDivElement>(null)

  useEffect(() => setMounted(true), [])

  // Anteprima live sull'header della pagina (stessa geometria del rendering).
  useEffect(() => {
    const layer = document.querySelector<HTMLElement>('[data-hero-image]')
    if (!layer || !hero) return
    applyHeroImageStyle(layer, hero)
  }, [hero])

  const loadMedia = useCallback(async () => {
    setLoadingList(true)
    setError(null)
    try {
      const res = await fetch(`/api/admin/media?clubId=${clubId}`, { cache: 'no-store' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Impossibile leggere la libreria')
      setMedia(data.media ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoadingList(false)
    }
  }, [clubId])

  async function toggle() {
    const next = !open
    setOpen(next)
    setNotice(null)
    if (next && media.length === 0) await loadMedia()
  }

  function pick(url: string) {
    setNotice(null)
    setHero((prev) => ({
      imageUrl: url,
      positionX: prev?.positionX ?? 50,
      positionY: prev?.positionY ?? 50,
      zoom: prev?.zoom ?? 1,
    }))
  }

  /** Sposta il focus in base alla posizione del puntatore dentro il joystick. */
  const moveFocus = useCallback((clientX: number, clientY: number) => {
    const el = padRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const pct = (v: number) => Math.round(Math.min(1, Math.max(0, v)) * 100)
    const positionX = pct((clientX - r.left) / r.width)
    const positionY = pct((clientY - r.top) / r.height)
    setHero((prev) => (prev ? { ...prev, positionX, positionY } : prev))
  }, [])

  async function save(remove = false) {
    setSaving(true)
    setError(null)
    setNotice(null)
    try {
      const payload = remove
        ? { imageUrl: '' }
        : {
            imageUrl: hero?.imageUrl ?? '',
            positionX: hero?.positionX ?? 50,
            positionY: hero?.positionY ?? 50,
            zoom: hero?.zoom ?? 1,
          }
      const res = await fetch(`/api/admin/clubs/${clubId}/hero`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error ?? 'Salvataggio non riuscito')
      setHero(data.hero ?? null)
      setNotice(remove ? 'Sfondo rimosso.' : 'Sfondo salvato.')
      router.refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setSaving(false)
    }
  }

  const panel = open ? (
    <div className="fixed bottom-4 right-4 z-50 max-h-[min(78vh,600px)] w-[330px] max-w-[calc(100vw-1.5rem)] overflow-y-auto rounded-2xl bg-[color:var(--league-surface)] p-4 text-[color:var(--league-ink)] shadow-[var(--league-shadow-lg)] ring-1 ring-black/5">
      <div className="flex items-start justify-between gap-3">
        <p className="league-kicker">Sfondo dell’header</p>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-[18px] leading-none text-[color:var(--league-muted)] hover:text-[color:var(--league-ink)]"
          aria-label="Chiudi"
        >
          ×
        </button>
      </div>

      <label className="mt-3 block">
        <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[color:var(--league-muted)]">
          Immagine
        </span>
        <select
          value={hero?.imageUrl ?? ''}
          onChange={(e) => pick(e.target.value)}
          className="mt-1 w-full rounded-lg border border-[color:var(--league-line)] bg-[color:var(--league-surface-alt)] px-3 py-2 text-[13px]"
        >
          <option value="">{loadingList ? 'Carico la libreria…' : 'Nessuna (gradiente di lega)'}</option>
          {hero?.imageUrl && !media.some((m) => m.url === hero.imageUrl) && (
            <option value={hero.imageUrl}>Immagine attuale</option>
          )}
          {media.map((m) => (
            <option key={m.id} value={m.url}>
              {m.originalName}
              {m.width && m.height ? ` (${m.width}×${m.height})` : ''}
            </option>
          ))}
        </select>
      </label>

      {hero?.imageUrl && (
        <>
          <p className="mt-3 text-[12px] font-semibold uppercase tracking-[0.12em] text-[color:var(--league-muted)]">
            Focus · trascina il mirino
          </p>
          {/* Joystick 2D: il riquadro mostra la foto intera, il mirino è il punto che
              resta al centro dell'header. Frecce della tastiera per la precisione. */}
          <div
            ref={padRef}
            role="application"
            tabIndex={0}
            aria-label="Focus immagine: trascina per spostare, frecce per la precisione"
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture(e.pointerId)
              moveFocus(e.clientX, e.clientY)
            }}
            onPointerMove={(e) => {
              if (e.currentTarget.hasPointerCapture(e.pointerId)) moveFocus(e.clientX, e.clientY)
            }}
            onKeyDown={(e) => {
              const step = e.shiftKey ? 1 : 5
              const delta =
                e.key === 'ArrowLeft' ? [-step, 0] :
                e.key === 'ArrowRight' ? [step, 0] :
                e.key === 'ArrowUp' ? [0, -step] :
                e.key === 'ArrowDown' ? [0, step] : null
              if (!delta) return
              e.preventDefault()
              setHero((prev) =>
                prev
                  ? {
                      ...prev,
                      positionX: Math.min(100, Math.max(0, prev.positionX + delta[0])),
                      positionY: Math.min(100, Math.max(0, prev.positionY + delta[1])),
                    }
                  : prev,
              )
            }}
            className="relative mt-2 h-[190px] w-full cursor-crosshair touch-none overflow-hidden rounded-xl border border-[color:var(--league-line)] bg-[color:var(--league-surface-alt)] outline-none focus:ring-2 focus:ring-[color:var(--league-brand-vivid)]"
            style={{
              backgroundImage: `url(${hero.imageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            {/* mirino */}
            <span
              aria-hidden
              className="pointer-events-none absolute h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,.45)]"
              style={{ left: `${hero.positionX}%`, top: `${hero.positionY}%`, backgroundColor: 'rgba(255,255,255,.18)' }}
            />
            <span aria-hidden className="pointer-events-none absolute inset-x-0 top-1/2 h-px bg-white/35" />
            <span aria-hidden className="pointer-events-none absolute inset-y-0 left-1/2 w-px bg-white/35" />
          </div>

          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-[12px] text-[color:var(--league-muted)]">
              {hero.positionX}% / {hero.positionY}%
            </span>
            <button
              type="button"
              onClick={() => setHero({ ...hero, positionX: 50, positionY: 50, zoom: 1 })}
              className="text-[12px] font-semibold text-[color:var(--league-brand-vivid)]"
            >
              Ripristina focus e zoom
            </button>
          </div>

          <label className="mt-3 block">
            <span className="text-[12px] text-[color:var(--league-muted)]">Zoom · {hero.zoom.toFixed(2)}×</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={hero.zoom}
              onChange={(e) => setHero({ ...hero, zoom: Number(e.target.value) })}
              className="mt-1 w-full"
            />
          </label>
        </>
      )}

      {error && <p className="mt-3 text-[12px] text-red-500">{error}</p>}
      {notice && !error && <p className="mt-3 text-[12px] text-green-600">{notice}</p>}

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={saving || !hero?.imageUrl}
          onClick={() => void save(false)}
          className="min-h-10 rounded-xl bg-[color:var(--league-brand-solid)] px-4 text-[13px] font-semibold text-white disabled:opacity-50"
        >
          {saving ? 'Salvo…' : 'Salva'}
        </button>
        <button
          type="button"
          disabled={saving}
          onClick={() => void save(true)}
          className="min-h-10 rounded-xl border border-[color:var(--league-line)] px-4 text-[13px] font-semibold text-[color:var(--league-ink)] disabled:opacity-50"
        >
          Rimuovi sfondo
        </button>
      </div>

      <p className="mt-3 text-[11px] leading-4 text-[color:var(--league-muted)]">
        Le immagini si caricano da <strong>Contenuti → Immagini</strong>. Il gradiente di lega resta sopra
        la foto per tenere leggibile il testo bianco.
      </p>
    </div>
  ) : null

  return (
    <>
      <button
        type="button"
        onClick={() => void toggle()}
        className="absolute bottom-3 right-3 z-20 inline-flex items-center gap-1.5 rounded-full bg-black/45 px-3 py-1.5 text-[12px] font-semibold text-white backdrop-blur transition-colors hover:bg-black/65"
        title="Modifica lo sfondo dell'header"
      >
        <svg aria-hidden viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 20h4l10-10-4-4L4 16v4Z" strokeLinejoin="round" />
          <path d="m13.5 6.5 4 4" />
        </svg>
        {open ? 'Chiudi' : 'Sfondo'}
      </button>

      {/* Fuori dall'header (che ha `overflow-hidden`): altrimenti il pannello è tagliato. */}
      {mounted && panel ? createPortal(panel, document.body) : null}
    </>
  )
}
